"use client";

import { useCallback, useRef } from "react";
import { useAction, useMutation } from "convex/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";

const UPLOAD_TOKEN_TTL_MS = 60 * 60 * 1000;
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function useGuestUploadToken() {
  const uploadTokenRef = useRef<{ token: string; issuedAt: number } | null>(
    null,
  );
  const issueToken = useMutation(api.files.mutations.issueGuestUploadToken);
  const generateGuestUrl = useMutation(
    api.files.mutations.generateUploadUrlForGuest,
  );
  const attestProductImage = useAction(api.products.imageAssets.attestUpload);

  const ensureFreshToken = useCallback(async () => {
    const existing = uploadTokenRef.current;
    if (
      existing &&
      Date.now() - existing.issuedAt <= UPLOAD_TOKEN_TTL_MS - REFRESH_BUFFER_MS
    ) {
      return existing.token;
    }
    const token = await issueToken({});
    uploadTokenRef.current = { token, issuedAt: Date.now() };
    return token;
  }, [issueToken]);

  const generateUploadUrl = useCallback(async () => {
    const token = await ensureFreshToken();
    return await generateGuestUrl({ uploadToken: token });
  }, [ensureFreshToken, generateGuestUrl]);

  const attestUpload = useCallback(
    async (storageId: Id<"_storage">) => {
      const uploadToken = await ensureFreshToken();
      return await attestProductImage({ storageId, uploadToken });
    },
    [attestProductImage, ensureFreshToken],
  );

  const invalidateToken = useCallback(() => {
    uploadTokenRef.current = null;
  }, []);

  return { attestUpload, ensureFreshToken, generateUploadUrl, invalidateToken };
}
